import { inputFieldsValidator } from './input-layout-validator';
import {
  OrderDetails,
  DemographicsInformation,
  InputFileInformation,
  AttributesAndBundlesInformation,
  Contact,
  FieldsInformation,
  OutputLayoutInformation,
  ScoresAndAddonProductsInformation,
  AttributeLike,
  ReportsAttributeLike,
} from '../contracts';
import { ScoreLookup } from '../../../lookups';
import { FieldDelimiter, OutputLayoutField } from '../../../fields';
import {
  OutputLocation,
  OutputTargetParty,
  DeliveryFileInfo,
} from '../../../output-location';
import { SSARProgramStatus, SSARProgram } from '../../../programs';
import { programeNameValidator } from '../../validators/common';
import { FullAttribute } from '../../../services';
import * as yup from 'yup';

const getScoreName = (
  scoresLookups: ScoreLookup[],
  scoreCode: string,
): string => scoresLookups.find((a) => a.code === scoreCode)?.name || scoreCode;

type AddonProductDependencies = Record<
  string,
  (scores: string[], scoresLookups: ScoreLookup[]) => string | undefined
>;

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

export const MaxAllowedAttributes = 3000;
export const MaxAllowedScoreDistributioReportsAttributes = 10;
export const MaxAttributesForDelimiterSelection = 1000;
export const contactValidator = yup.object<Contact>({
  name: yup.string().label('Name').required(),
  clientName: yup.string().label('Client Name').required(),
  email: yup.string().label('Email').email().required(),
  address: yup.string().label('Address').required(),
  city: yup.string().label('City').required(),
  state: yup.string().label('State').required(),
  zipCode: yup.string().label('Zip Code'),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  phoneNumber: yup.lazy((value: any) => {
    // console.log('Phone Number:', value, ' of value ', !value);
    return !value || value.toString().length < 1
      ? yup.string()
      : yup
          .string()
          .label('Phone number')
          .notRequired()
          .matches(phoneRegExp, 'Phone number "${value}" is not valid');
  }),
});

export const tmsNumberValidator = yup
  .string()
  .label('TMS Number')
  .required()
  .min(1)
  .max(8)
  .test({
    name: 'validTMSNumber',
    test: (val: string) => /^[0-9]{1,8}$/.test(val),
    message:
      '"${value}" is not a valid TMS Number. A TMS Number should have 1 to 8 digits',
  });

export const orderDetailsValidator = yup.object<OrderDetails>({
  name: programeNameValidator,
  tmsNumber: tmsNumberValidator,
  contact: contactValidator.required(),
});

const aisAddonProductValidation = (
  selectedScores: string[],
  scoresLookups: ScoreLookup[],
) => {
  const requiredScores = ['AF', 'F9', 'F', 'Q3', 'Q4'];
  const includedScores = requiredScores.filter((score) =>
    selectedScores.includes(score),
  );
  if (includedScores.length < 1) {
    return `At least one of the ${requiredScores
      .map((score) => getScoreName(scoresLookups, score))
      .join(', ')} scores should be selected when AIS products are selected`;
  }
};

const AIS_Prefix_Lookup_String = 'Append_AIS';

const addonProductDependencies: AddonProductDependencies = {
  [AIS_Prefix_Lookup_String]: (...params) =>
    aisAddonProductValidation(...params),
};

export const addonProductsSchemaValdiator = yup
  .object<{ addOnProducts: string[]; mappedHeaders: string[] }>()
  .test({
    name: 'addonProductsValidation',
    test({ addOnProducts, mappedHeaders }) {
      const aisProduct = (addOnProducts || []).filter(
        (product: string) => product.indexOf(AIS_Prefix_Lookup_String) !== -1,
      );
      if (aisProduct.length > 0) {
        const requiredInputFields = ['OPEN_DATE', 'CREDIT_LIMIT', 'BALANCE'];
        const missingFlds = requiredInputFields.filter(
          (reqFld) => mappedHeaders.indexOf(reqFld) === -1,
        );

        return missingFlds.length === 0
          ? true
          : this.createError({
              message: `${missingFlds.join(
                ', ',
              )} should be mapped if AIS products are selected.`,
            });
      } else {
        return true;
      }
    },
  });

export const scoresAndAddonProductsSchemaValdiator =
  yup.object<ScoresAndAddonProductsInformation>({
    scores: yup
      .array<string>()
      .label('Scores')
      .default([])
      // .required()
      // .min(1, 'At least one score/model should be selected')
      .max(10, 'At most 10 scores/models can be selected')
      .test({
        name: 'scoresCombinations',
        test(scores: string[]) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const context = this.options.context as any;
          const scoresLookups: ScoreLookup[] = context.getScoresLookups
            ? context.getScoresLookups() || []
            : [];
          const scoreDependencies = [
            {
              isRequired: 'II',
              whenSelected: ['D1', 'D2', 'D3'],
            },
            {
              isRequired: 'W2',
              whenSelected: ['WT', 'WF', 'WJ', 'WM'],
            },
            {
              isRequired: 'BT',
              whenSelected: ['BS'],
            },
          ];
          const errorMapings: Record<string, string[]> = {};
          scoreDependencies.forEach((a) => {
            if (!errorMapings[a.isRequired]) {
              errorMapings[a.isRequired] = [];
            }
            a.whenSelected.forEach((entry) => {
              if (scores.includes(entry) && !scores.includes(a.isRequired)) {
                errorMapings[a.isRequired].push(entry);
              }
            });
          });
          const errors = Object.keys(errorMapings)
            .map((key) => {
              const values = errorMapings[key];
              return { key, values: values || [] };
            })
            .filter((a) => a.values.length > 0)
            .map((entry) => {
              const required = getScoreName(scoresLookups, entry.key);
              const whenSelected = entry.values.map((a) =>
                getScoreName(scoresLookups, a),
              );
              return `${required} should be selected when ${whenSelected.join(
                ', ',
              )} ${whenSelected.length > 1 ? 'are' : 'is'} selected`;
            });

          // Add-on product dependencies
          const addonProducts: string[] = this.parent.addonProducts;
          const products = Array.from(
            new Set(
              addonProducts
                .map(
                  (product) =>
                    product?.split('_')?.slice(0, -1)?.join('_') || '',
                )
                .filter((a) => !!a),
            ).values(),
          );
          products.forEach((productPrefix) => {
            const validation = addonProductDependencies?.[productPrefix];
            if (validation) {
              const validationResult = validation(scores, scoresLookups);
              if (validationResult) {
                errors.push(validationResult);
              }
            }
          });

          return errors.length
            ? this.createError({
                message: errors.join(', '),
              })
            : true;
        },
      }),

    addonProducts: yup.array<string>().label('Add-On Products').default([]),
  });

export const demographicsSchemaValidator = yup.object<DemographicsInformation>({
  shouldAppendDOBCCYYMMDD: yup.boolean().strict(true),
  shouldAppendDOBMMDDCCYY: yup.boolean().strict(true),
  isEmploymentDataSelected: yup.boolean().strict(true),
  isPhoneNumberDataSelected: yup.boolean().strict(true),
  isExperianSocialSelected: yup.boolean().strict(true),
  isMlaInSelected: yup.boolean().strict(true),
});

const oneOfEnum = <T>(enumObject: { [s: string]: T } | ArrayLike<T>) =>
  yup.mixed<T>().oneOf(Object.values(enumObject));

const fieldsInfoValidator = yup.object<FieldsInformation>({
  source: yup.string().oneOf(['Inline']).required(),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: inputFieldsValidator.label('Fields')?.default([]) as any,
});

export const inputFileInformationValidator = yup.object<InputFileInformation>({
  fileName: yup.string().label('File name').required(),
  clientFileName: yup.string().label('Client File name').required(),
  numberOfRecordsExpected: yup
    .number()
    .nullable(true)
    .transform((v) => {
      return v === '' || isNaN(v) ? null : v;
    })
    .positive()
    .min(1)
    .max(999999999999)
    .label('Number of records')

    .required(),
  fieldsInfo: fieldsInfoValidator.required(),
  hasHeaders: yup.boolean().label('Has Headers?'),
  delimiter: oneOfEnum(FieldDelimiter).label('Delimiter').required(),
  isRemoteUploadedFile: yup.boolean().label('Remote Upload File?').required(),
});

const deliverFileInfoValidator = yup.object<DeliveryFileInfo>({
  isNeeded: yup.boolean().label('File Required').default(false).required(),
  name: yup
    .string()
    .label('File name')
    .when('isNeeded', {
      is: true,
      then: yup
        .string()
        .required()
        .min(3)
        .test({
          name: 'validFileName1',
          test: (val: string) => {
            return val?.trim().length >= 3;
          },
          message: 'File name must be at least 3 characters',
        })
        .test({
          name: 'validFileName2',
          test: (val: string) => {
            return val?.length <= 40;
          },
          message: 'File name must be at most 40 characters',
        })
        .test({
          name: 'validFileName3',
          test: (val: string) => {
            return val?.indexOf(' ') === -1;
          },
          message: "File name shouldn't contain spaces.",
        }),
      otherwise: yup.string().notRequired(),
    }),
});

export const outputLocationValidator = yup
  .object<OutputLocation>()
  .shape({
    target: oneOfEnum(OutputTargetParty).label('Target').required(),
    delimiter: oneOfEnum(FieldDelimiter).label('Delimiter').required(),
    mainFile: deliverFileInfoValidator,
    rejectFile: deliverFileInfoValidator,
    statementFile: deliverFileInfoValidator,
    location: yup
      .string()
      .label('Location')
      .nullable()
      .required(
        'You must select a contact and output folder to receive your output file(s).',
      )
      .min(1),
    zippedFile: deliverFileInfoValidator,
    contact: contactValidator.required(),
  })
  .test({
    name: 'outputFiles',
    test(val: Partial<OutputLocation>) {
      const isValid: boolean =
        val?.mainFile?.isNeeded ||
        val?.rejectFile?.isNeeded ||
        val?.statementFile?.isNeeded ||
        false;
      const fileNamesArray: string[] = [
        val.mainFile?.name ? val.mainFile?.name.trim() : '',
        val.rejectFile?.name ? val.rejectFile?.name.trim() : '',
        val.statementFile?.name ? val.statementFile?.name.trim() : '',
        val.zippedFile?.name ? val.zippedFile?.name.trim() : '',
      ];
      const noWhiteSpaceArray = fileNamesArray.filter(
        (entry) => entry.trim() !== '',
      );
      const areValidFileNames = noWhiteSpaceArray.some(
        (x) =>
          noWhiteSpaceArray.indexOf(x) !== noWhiteSpaceArray.lastIndexOf(x),
      );
      const toReturn: boolean | yup.ValidationError = isValid
        ? !areValidFileNames
          ? true
          : this.createError({
              path: 'outputFiles', // this.path,
              message:
                'Each file delivered to the same Output Location must have a unique name.',
            })
        : this.createError({
            path: 'outputFiles',
            message:
              'Select at least one of the Main/Reject/Statement file(s).',
          });
      return toReturn;
    },
  });

export const outputLocationsInfoValidator = yup
  .array<OutputLocation>(outputLocationValidator.required())
  .label('Output Locations')
  .min(1)
  .max(3)
  .required()
  .test({
    name: 'outputFiles',
    test(val: Partial<OutputLocation>[]) {
      const isMainFileSelected = val.some((a) => a?.mainFile?.isNeeded);
      const isStatementFileSelected = val.some(
        (a) => a?.statementFile?.isNeeded,
      );
      const isValid: boolean = isMainFileSelected && isStatementFileSelected;
      // console.log('vvv: ', isValid, val);
      const toReturn = isValid
        ? isValid
        : this.createError({
            path: 'outputFiles', // this.path,
            message:
              'At least one Main and one Statement file should be selected across all output locations.',
          });
      // console.log(toReturn);
      return toReturn;
    },
  });

const outputFieldValidator = yup.object<OutputLayoutField>({
  name: yup.string().label('Field Name').notRequired(),
  fieldLength: yup.number().label('Field Length').required().min(1).max(1000),
  headerName: yup.string().label('Client Header').notRequired(),
  source: yup.string().oneOf(['Experian', 'Client', 'User']).required(),
  outputOrder: yup.number().required(),
  category: yup.string().required(),
  meta: yup.mixed(),
});

export const outputLayoutInformationValidator =
  yup.object<OutputLayoutInformation>({
    delimiter: oneOfEnum(FieldDelimiter).label('Delimiter').required(),
    fields: yup
      .array<OutputLayoutField>(outputFieldValidator.required())
      .required()
      .min(1),
    isCustomized: yup.boolean().default(false),
  });

const attributeLikeValidator: yup.Schema<AttributeLike> = yup
  .object<AttributeLike>({
    code: yup.string().label('Attribute Code').required(),
    category: yup.string().label('Attribute Category').required(),
    group: yup.string().label('Attribute Group').required(),
  })
  .required();

const fullAttributeValidator: yup.Schema<FullAttribute> = yup
  .object<FullAttribute>({
    displayName: yup.string().label('Complete Attribute Name').required(),
    code: yup.string().label('Complete Attribute Code').required(),
    description: yup.string().label('Complete Attribute Description'),
    attributeGroups: yup
      .array<string>()
      .label('Complete Attribute groups')
      .required(),
  })
  .required();

export const reportsInfoValidator = yup.object<ReportsAttributeLike>().default({
  distribution: {
    attributes: [],
  },
});
export const attributesAndBundlesInfoValidator =
  yup.object<AttributesAndBundlesInformation>({
    bundles: yup.array<string>().default([]).label('Bundles'),
    fullAttributes: yup
      .array<FullAttribute>(fullAttributeValidator)
      .default([])
      .label('Complete Attribute Sets'),
    attributes: yup
      .array<AttributeLike>(attributeLikeValidator)
      .label('Attributes')
      .notRequired()
      .max(
        MaxAllowedAttributes,
        `At most ${MaxAllowedAttributes} attributes can be selected.`,
      ),
  });

export const ssarPreSubmitValidator = yup
  .object<SSARProgram>()
  .test({
    name: 'ssarCreditAttributeValidation',
    test(program?: SSARProgram) {
      const creditAttributesCount =
        (program?.attributesAndBundles?.attributes?.length || 0) +
        (program?.attributesAndBundles?.fullAttributes?.length || 0) +
        (program?.attributesAndBundles?.bundles?.length || 0) +
        (program?.scoresAndAddonProducts?.scores?.length || 0);
      return creditAttributesCount === 0
        ? this.createError({
            message:
              'At least one credit attribute (one of Scores or Attributes) should be selected.',
          })
        : true;
    },
  })
  .test({
    name: 'addonProductsValidation',
    test(program?: SSARProgram) {
      const addOnProductsExists =
        program?.scoresAndAddonProducts?.addonProducts?.length || false;
      if (addOnProductsExists) {
        const requiredInputFields = ['OPEN_DATE', 'CREDIT_LIMIT', 'BALANCE'];
        const headers =
          program?.inputFile?.fieldsInfo?.fields?.map((field) => field.name) ||
          [];
        const hasRequiredInputFields = requiredInputFields.every((field) =>
          headers.includes(field),
        );

        return hasRequiredInputFields
          ? true
          : this.createError({
              message: `${requiredInputFields.join(
                ', ',
              )} should be mapped in the selected input file if AIS products are selected.`,
            });
      } else {
        return true;
      }
    },
  });

export const ssarProgramV1Validator = yup
  .object<SSARProgram>({
    status: oneOfEnum(SSARProgramStatus).label('Status').required(),
    programKey: yup
      .string()
      .label('Program Key')
      .required('Program Key is required, this is usually auto populated.'),
    subCode: yup
      .string()
      .label('Subscribe Code')
      .required()
      .length(7)
      .matches(/^\d{7}$/, 'Subscriber Code value: "${value}" is invalid.'),
    orderDetails: orderDetailsValidator.required(),
    scoresAndAddonProducts: scoresAndAddonProductsSchemaValdiator.required(),
    demographics: demographicsSchemaValidator.required(),
    inputFile: inputFileInformationValidator.required(),
    outputLocations: outputLocationsInfoValidator.required(),
    attributesAndBundles: attributesAndBundlesInfoValidator.required(),
    reports: reportsInfoValidator.required(),
    outputLayout: outputLayoutInformationValidator.required(),
    version: yup.string().oneOf(['v1']).required(),
  })
  .concat(ssarPreSubmitValidator);

export { inputFieldsCombinationsValidator as ascendQuestInputFieldsCombinationsValidator } from './input-layout-validator';
